"use client"

import LinkButton from "components/Buttons/Link"
import gsap from "gsap/all"
import EmtypCircleLogo from "images/Icons/EmptyCircle.inline.svg"
import IlloFgIMG from "images/about/arloDefined/Foreground.webp"
import IlloBgIMG from "images/home/advantage/background.png"
import LineSVG from "images/home/advantage/line.inline.svg"
import StaticImage from "library/StaticImage"
import useCanHover from "library/canHover"
import { css, f, styled } from "library/styled"
import { useAnimation } from "library/useAnimation"
import { useMedia } from "library/useMedia"
import { useRef } from "react"
import colors from "styles/colors"
import textStyles from "styles/text"

const MOVEMENT_PERCENT = -2

type AdvantageInfo = {
	heading: string
	text: string
	image: string
	number: string
	about?: {
		text: string
		link: string
	}
}

const defaultInfo: AdvantageInfo = {
	heading: "The Arlo Advantage",
	text: "Traditional insurers profit from denied care. Arlo flips the model—using AI to give small businesses enterprise-level strategies that balance savings and long-term health. We align incentives with outcomes, helping employers escape the insurance churn and invest in their teams. Backed by experts, we're transforming the $275B small group market with a simple belief: prevention should pay.",
	image: IlloFgIMG.src,
	number: "01",
}

export default function AdvantageContent({
	info = defaultInfo,
	reversed = false,
}: {
	info: AdvantageInfo
	reversed?: boolean
}) {
	const foregroundRef = useRef<HTMLImageElement>(null)
	const wrapperRef = useRef<HTMLDivElement>(null)
	const innerRef = useRef<HTMLDivElement>(null)
	const canHover = useCanHover()
	const linkSize = useMedia("large", "large", "large", "small")

	useAnimation(() => {
		if (!foregroundRef.current || !wrapperRef.current) return

		const settings = {
			duration: 1.5,
		}

		const foregroundX = gsap.quickTo(
			foregroundRef.current,
			"xPercent",
			settings,
		)
		const foregroundY = gsap.quickTo(
			foregroundRef.current,
			"yPercent",
			settings,
		)

		const handleMouseMove = (e: { clientX: number; clientY: number }) => {
			const mouseX = (e.clientX / window.innerWidth - 0.5) * 2
			const mouseY = (e.clientY / window.innerHeight - 0.5) * 2

			foregroundX(mouseX * MOVEMENT_PERCENT)
			foregroundY(mouseY * MOVEMENT_PERCENT + MOVEMENT_PERCENT)
		}

		if (canHover) {
			window.addEventListener("mousemove", handleMouseMove)
		}

		return () => {
			window.removeEventListener("mousemove", handleMouseMove)
		}
	}, [canHover])

	const { heading, text, image, number, about } = info

	return (
		<Wrapper ref={wrapperRef}>
			<Inner reversed={reversed} ref={innerRef}>
				<Illustration>
					<IllustrationInner>
						<IllustrationBg src={IlloBgIMG} alt="" />
						<IllustrationFg
							width={586}
							height={382}
							ref={foregroundRef}
							src={image}
							alt={text}
						/>
					</IllustrationInner>
				</Illustration>

				<LineContainer>
					<LineSVG />
				</LineContainer>
				<NumberTag reversed={reversed}>
					<InnerTag>
						<EmtypCircleLogo />
						{number}
					</InnerTag>
				</NumberTag>
				<Content reversed={reversed}>
					<H4>{heading}</H4>
					<Text>{text}</Text>
					{about && (
						<StyledLinkButton size={linkSize} variant="dark" href={about.link}>
							{about.text}
						</StyledLinkButton>
					)}
				</Content>
			</Inner>
		</Wrapper>
	)
}

const Wrapper = styled("div", {
	...f.responsive(css`
		position: relative;
		height: 100vh;
		z-index: 0;
		display: grid;
		place-content: center;
	`),

	...f.tablet(css`
		height: 100lvh;
		padding-top: 100px;
	`),

	...f.mobile(css`
		position: relative;
		height: auto;
		padding-top: 0;
		margin-bottom: 140px;
	`),
})

const Inner = styled("div", ({ reversed }: { reversed?: boolean }) => ({
	...f.responsive(css`
		width: 1440px;
		align-items: center;
		justify-content: space-between;
		display: flex;
		flex-direction: ${reversed ? "row-reverse" : "row"};
		height: fit-content;
		margin: 0 auto;
		position: relative;
		z-index: 2;
		padding: 0 32px;
	`),
	...f.tablet(css`
		width: 1024px;
		flex-direction: column;
		justify-content: flex-start;
		height: 732px;
		gap: 56px;
	`),
	...f.mobile(css`
		width: 100%;
		padding: 0;
		flex-direction: column;
		gap: 32px;
	`),
}))

const Content = styled("div", ({ reversed }: { reversed?: boolean }) => ({
	...f.responsive(css`
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		width: 610px;
		height: 100%;
		gap: 24px;
		padding-left: ${reversed ? "192px" : "0"};
	`),
	...f.tablet(css`
		width: 630px;
		padding-left: 180px;
		height: auto;
	`),
	...f.mobile(css`
		position: relative;
		width: 327px;
		height: auto;
		justify-content: unset;
		align-items: center;
		padding-left: 0;
	`),
}))

const Illustration = styled("div", {
	...f.unresponsive(css`
		border: 1px solid ${colors.green100};
	`),
	...f.responsive(css`
		width: 618px;
		height: 406px;
		border-radius: 18px;
		border: 1px solid ${colors.greenAccent};
		padding: 12px;
		position: relative;
		overflow: clip;
		z-index: 0;
		pointer-events: none;
	`),

	...f.tablet(css`
		background: ${colors.greenMain};
	`),

	...f.mobile(css`
		width: 327px;
		height: 256px;
	`),
})

const IllustrationInner = styled("div", {
	...f.responsive(css`
		width: 100%;
		height: 100%;
		border-radius: 12px;
		background: ${colors.green100Secondary};
		padding: 12px;
		position: relative;
		overflow: clip;
	`),
})

const illustrationBase = css`
	display: block;
	position: absolute;
	top: 0;
	left: 0;
`

const IllustrationFg = styled(StaticImage, {
	...f.responsive(css`
		${illustrationBase};
		width: 586px;
		height: 382px;
		bottom: 0;
		top: unset;
		max-inline-size: unset;
		max-block-size: unset;
	`),

	...f.tablet(css`
		width: 586px;
		height: 382px;
		top: 0;
		left: 0;
	`),

	...f.mobile(css`
		width: 303px;
		height: 232px;
	`),
})

const IllustrationBg = styled(StaticImage, {
	...f.responsive(css`
		${illustrationBase};
		width: 586px;
		height: 382px;
	`),
	...f.mobile(css`
		display: none;
	`),
})

const H4 = styled("p", {
	...f.responsive(css`
		white-space: pre-line;
		${textStyles.h5};
		color: ${colors.white};
		width: 450px;
	`),
	...f.tablet(css`
		${textStyles.h5};
		width: 100%;
	`),
	...f.mobile(css`
		${textStyles.bodyL};
		width: 287px;
	`),
})

const Text = styled("p", {
	...f.responsive(css`
		${textStyles.bodyR};
		color: ${colors.green100};
		width: 438px;
	`),
	...f.tablet(css`
		width: 400px;
	`),
	...f.mobile(css`
		${textStyles.bodyS};
		width: 287px;
	`),
})

const NumberTag = styled("div", ({ reversed }: { reversed?: boolean }) => ({
	...f.responsive(css`
		position: relative;
		width: 60px;
		height: 60px;
		border: 1px solid ${colors.greenAccent};
		border-radius: 12px;
		background: ${colors.greenMain};
		display: grid;
		place-content: center;
		justify-items: center;
		color: ${colors.white};
		padding: 5px;
		${textStyles.bodyXXS};
		transform: translateX(${reversed ? "4px" : "-4px"});

		svg {
			width: 32px;
			height: 32px;
			color: ${colors.white};
			position: absolute;
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%);
		}
	`),

	...f.tablet(css`
		position: absolute;
		left: 259px;
		top: 460px;
		transform: translateX(${reversed ? "-3px" : "-4px"});
	`),

	...f.mobile(css`
		display: none;
	`),
}))

const InnerTag = styled("div", {
	...f.responsive(css`
		position: relative;
		width: 50px;
		height: 50px;
		border-radius: 8px;
		background: ${colors.green100Secondary};
		display: grid;
		place-content: center;
		color: ${colors.white};
	`),
})

const LineContainer = styled("div", {
	...f.responsive(css`
		position: absolute;
		width: 2px;
		height: 384px;
		z-index: -1;
		left: 50%;
		transform: translateX(-50%);

		svg {
			width: 100%;
			height: 100%;
			position: absolute;
			top: 0;
			left: 0;
			z-index: -1;
		}
	`),

	...f.tablet(css`
		left: 284px;
		top: 340px;
		transform: translateX(0);
	`),

	...f.mobile(css`
		display: none;
	`),
})

const StyledLinkButton = styled(LinkButton, {
	...f.desktop(css`
		align-self: flex-start;
		margin-left: -14px;
		padding-right: 24px;
		gap: 0;
	`),
	...f.mobile(css`
		align-self: flex-start;
		margin-left: 20px;
	`),
})
